/*
 * (c) Copyright, 2026, Medidata Solutions Worldwide, Inc. All rights reserved.
 *
 * PropertySearchRowMenu - Row action menu component for PropertySearchDataGrid
 */

import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Dropdown } from '@mdsol/onex-design'

import { useRcmTableData } from '../../hooks/useRcmTableData'
import { useFsiActionMenu } from '../../hooks/useFsiActionMenu'
import usePropertySearchService from '../../hooks/usePropertySearchService'
import { transformToDropdownItems } from './propertySearchDataGridUtils'
import type { PropertySearchRowMenuProps } from './propertySearchDataGridTypes'

const PropertySearchRowMenu: React.FC<PropertySearchRowMenuProps> = ({
  row,
  selectedIds,
  selectedItems,
  deselectAll,
  parentFsi,
  isAllDataSelected,
  totalCount
}) => {
  const { t } = useTranslation()
  const fsiItem = row.original

  // Is this row part of the current multi-selection?
  const isRowInSelection = selectedIds.includes(fsiItem.id)
  const hasMultipleSelected = selectedIds.length > 1

  // Build options for useFsiActionMenu
  // Only pass selectedIds if this row is in selection AND multiple items selected
  const { assembleFiltersFromFormValues } = usePropertySearchService()
  const tableData = useRcmTableData()

  // Assemble the current filter so modals (Make Obsolete, QC Review, etc.) can send it to the API
  const assembledFilter = useMemo(() => {
    if (!tableData.filter || Object.keys(tableData.filter).length === 0) return undefined
    return assembleFiltersFromFormValues({ ...tableData.filter })
  }, [tableData.filter, assembleFiltersFromFormValues])

  const menuOptions = useMemo(
    () => ({
      selectedIds: isRowInSelection && hasMultipleSelected ? selectedIds : undefined,
      selectedItems: isRowInSelection && hasMultipleSelected ? selectedItems : undefined,
      selectedWholeResult: isAllDataSelected,
      totalItemsInSection: totalCount ?? undefined,
      parentFolderId: parentFsi?.id,
      isPropertiesSearch: true,
      deselectAll,
      filter: assembledFilter,
      meta: {}
    }),
    [
      selectedIds,
      selectedItems,
      isRowInSelection,
      hasMultipleSelected,
      parentFsi?.id,
      deselectAll,
      isAllDataSelected,
      assembledFilter
    ]
  )

  const { items, isOpen, setIsOpen } = useFsiActionMenu(fsiItem, menuOptions)

  const isLoading = items.length === 1 && items[0]?.value === 'LOADING...'

  const dropdownItems = useMemo(() => {
    if (isLoading) return []
    if (items.length === 0) return []
    return transformToDropdownItems(items, t)
  }, [items, isLoading, t])

  // Don't show menu for invalid items
  if (!fsiItem.valid) return null

  return (
    <Dropdown
      id={`action-menu-${fsiItem.id}`}
      dataTestId={`action-menu-${fsiItem.id}`}
      align="end"
      size="sm"
      variant="tertiary"
      buttonStyle="icon"
      iconType="more_vert"
      items={dropdownItems}
      isLoading={isLoading}
      show={isOpen}
      onToggle={(nextShow: any) => setIsOpen(Boolean(nextShow))}
      renderOnMount={true}
      menuPortalTarget={document.body as any}
      popperConfig={{
        strategy: 'absolute',
        modifiers: [
          {
            name: 'preventOverflow',
            options: {
              boundary: 'viewport',
              padding: 8
            }
          }
        ]
      }}
    />
  )
}

export default PropertySearchRowMenu
